import { Bell, CheckCheck } from "lucide-react";
import {
  Button,
  CountBadge,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@facade/components/ui";
import { useMemo, useState } from "react";

import { NotificationFeed } from "../NotificationFeed";
import { notificationQueries } from "../../queries";
import { useMarkAllNotificationsReadMutation } from "../../mutations";
import { useQuery } from "@tanstack/react-query";

export function NotificationBell({ discrete = false }: { discrete?: boolean }) {
  const [open, setOpen] = useState(false);
  const unreadCountQuery = useQuery(notificationQueries.unreadCount());
  const markAllRead = useMarkAllNotificationsReadMutation();
  const params = useMemo(() => ({ limit: 20, unreadOnly: false }), []);
  const unreadCount = unreadCountQuery.data?.count ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={discrete ? "link" : "outline"}
          size="icon-lg"
          className="relative"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
        >
          <Bell className="size-4" />
          <CountBadge
            count={unreadCount}
            className="absolute -right-1 -top-1"
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[min(24rem,calc(100vw-1rem))] p-0"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Notifications</h2>
            <p className="text-xs text-muted-foreground">
              {unreadCount ? `${unreadCount} unread` : "You're all caught up"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            <CheckCheck />
            Mark all read
          </Button>
        </div>

        <NotificationFeed
          params={params}
          enabled={open}
          showProjectNames
          onNotificationSelect={() => setOpen(false)}
          className="max-h-[28rem] overflow-y-auto"
        />
      </PopoverContent>
    </Popover>
  );
}
